/**
 * Autonomous curriculum framework.
 * Surface at /academics/curriculum. The detailed per-branch syllabus
 * lives on each /department/:slug page.
 */

export interface CurriculumPillar {
  numeral: string;
  title: string;
  body: string;
}

export interface CreditRequirement {
  category: string;
  credits: string;
  detail: string;
}

export interface CourseCodingExample {
  code: string;
  meaning: string;
}

export const CURRICULUM_PILLARS: CurriculumPillar[] = [
  {
    numeral: "I",
    title: "Foundational rigor",
    body:
      "The first year is shared across branches and is unapologetically classical: mathematics, physics, chemistry, programming, engineering drawing, workshop. We do not 'apply' before students have built the foundation to apply from.",
  },
  {
    numeral: "II",
    title: "Branch depth",
    body:
      "Years two and three are branch-immersive. Each programme has its own core sequence — five to six courses per semester that build cumulatively. Electives appear from the fifth semester, not before.",
  },
  {
    numeral: "III",
    title: "Industry alignment",
    body:
      "The curriculum is reviewed annually with industry partners on each branch's Board of Studies. Course content that has gone stale is removed; emerging topics are introduced as electives first, core only after they survive two cycles of student feedback.",
  },
  {
    numeral: "IV",
    title: "Capstone autonomy",
    body:
      "The final year is structured around a year-long capstone — typically with a real industry partner or research centre. Students spend more time on the capstone than on any single course.",
  },
];

export const CREDIT_REQUIREMENTS: CreditRequirement[] = [
  { category: "Core courses", credits: "108–112", detail: "Branch-specific theory + laboratory" },
  { category: "Engineering electives", credits: "18–22", detail: "Within-branch electives, Sem 5 onward" },
  { category: "Open electives", credits: "9–12", detail: "Cross-branch electives, Sem 6–7" },
  { category: "Humanities & ethics", credits: "9", detail: "Mandatory, spread across all four years" },
  { category: "Internship", credits: "6", detail: "8-week mandatory internship after Sem 6" },
  { category: "Capstone project", credits: "12", detail: "Year-long final-year project" },
  { category: "Total (B.E.)", credits: "180", detail: "Required for B.E. degree" },
];

export const COURSE_CODING: CourseCodingExample[] = [
  { code: "CSE201", meaning: "Course offered by CSE · taken in 2nd year (Sem 3 or 4)" },
  { code: "ECE412", meaning: "Course offered by ECE · taken in 4th year (Sem 7 or 8)" },
  { code: "OE301", meaning: "Open elective · taken in 3rd year" },
  { code: "HU101", meaning: "Humanities course · taken in 1st year" },
];

export const CURRICULUM_PROCESS = [
  "Annual Board of Studies meeting per branch — held in May",
  "External industry reviewer attends each Board (rotates yearly)",
  "Proposed changes circulated to faculty for 30-day comment period",
  "Academic Council approval — June",
  "Revised curriculum effective from Aug term in the same calendar year",
];
